import { appConfig } from '../config';

export type ApiErrorKind = 'http' | 'network' | 'timeout' | 'parse' | 'unauthorized';

export class ApiError extends Error {
  readonly status?: number;
  readonly kind: ApiErrorKind;
  readonly details?: unknown;

  constructor(message: string, kind: ApiErrorKind, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.details = details;
  }
}

let inMemoryCsrfToken: string | null = null;

export const getCsrfToken = () => inMemoryCsrfToken;

export const setCsrfToken = (token: string | null) => {
  inMemoryCsrfToken = token;
};

const getErrorMessage = (payload: unknown, fallback: string) => {
  if (!payload || typeof payload !== 'object') return fallback;
  const object = payload as Record<string, unknown>;

  if (typeof object.detail === 'string') return object.detail;
  if (typeof object.title === 'string') return object.title;
  if (typeof object.message === 'string') return object.message;

  if (object.errors && typeof object.errors === 'object') {
    const messages = Object.values(object.errors as Record<string, unknown>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => typeof value === 'string');
    if (messages.length) return messages.join(' ');
  }

  return fallback;
};

const parseResponse = async (response: Response) => {
  if (response.status === 204) return undefined;

  const text = await response.text();
  if (!text) return undefined;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError('Сервер вернул ответ в неизвестном формате.', 'parse', response.status);
  }
};

const resolveUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${appConfig.apiUrl}${normalizedPath}`;
};

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeoutMs?: number;
  authenticated?: boolean;
}

export const apiRequest = async <T>(path: string, options: ApiRequestOptions = {}, csrfRetried = false): Promise<T> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort('timeout'),
    options.timeoutMs ?? appConfig.requestTimeoutMs,
  );
  const authenticated = options.authenticated ?? true;
  const headers = new Headers(options.headers);
  const csrfToken = getCsrfToken();
  const method = (options.method || 'GET').toUpperCase();
  const isSafeMethod = method === 'GET' || method === 'HEAD' || method === 'OPTIONS' || method === 'TRACE';

  headers.set('Accept', 'application/json');

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  if (authenticated && !isSafeMethod && csrfToken) {
    headers.set('X-KorsaApp-CSRF', csrfToken);
  }

  if (options.signal) {
    if (options.signal.aborted) controller.abort(options.signal.reason);
    options.signal.addEventListener('abort', () => controller.abort(options.signal?.reason), { once: true });
  }

  try {
    const response = await fetch(resolveUrl(path), {
      ...options,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      headers,
      credentials: 'include',
      signal: controller.signal,
    });
    const payload = await parseResponse(response);

    if (!response.ok) {
      // This middleware rejection happens before the endpoint runs, so replaying once cannot duplicate a mutation.
      if (!csrfRetried && authenticated && !isSafeMethod && response.status === 403 &&
        payload && typeof payload === 'object' && (payload as Record<string, unknown>).title === 'CSRF validation failed') {
        const session = await apiRequest<{ csrfToken: string }>('/api/web-auth/session', {
          authenticated: false, signal: controller.signal,
        }).catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 401)
            window.dispatchEvent(new CustomEvent('korsaapp:unauthorized'));
          throw error;
        });
        setCsrfToken(session.csrfToken);
        return await apiRequest<T>(path, { ...options, signal: controller.signal }, true);
      }
      if (response.status === 401) {
        setCsrfToken(null);
        if (authenticated) window.dispatchEvent(new CustomEvent('korsaapp:unauthorized'));
        throw new ApiError(
          getErrorMessage(payload, 'Сессия завершена. Войдите снова.'),
          'unauthorized',
          response.status,
          payload,
        );
      }

      throw new ApiError(
        getErrorMessage(payload, `Ошибка сервера (${response.status}).`),
        'http',
        response.status,
        payload,
      );
    }

    return payload as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;

    if (controller.signal.aborted) {
      throw new ApiError('Сервер не ответил вовремя. Повторите попытку.', 'timeout');
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'Не удалось подключиться к серверу.',
      'network',
    );
  } finally {
    window.clearTimeout(timeout);
  }
};
